import {GridModel} from '@xh/hoist/cmp/grid';
import {HoistModel, managed, TaskObserver, XH} from '@xh/hoist/core';
import {action, observable} from '@xh/hoist/mobx';
import {NotificationMessage} from '../../../../data/MessageTypes';
import {WatchListUpdate, WatchListUpdates} from '../../../../data/DataTypes';
import {compactNumberFormatter, percentPlainFormatter} from '../../../../cmp/Formaters';
import {priceRenderer} from '@xh/hoist/format';
import {buildHeatmapScale, simulateBidAsk} from './WatchlistSimulation';
import {changeBlockRenderer, dayRangeRenderer, relVolumeRenderer, sparklineRenderer, yearRangeRenderer} from './WatchlistRenderers';
import {intradaySparkline} from '../../../../cmp/miniCharts/IntradaySparkline';
import {MINI_CHART_CELL} from '../../../../cmp/miniCharts/MiniCharts';

/** A ticker's daily statistics. Only the snapshot has them, and live updates leave them out. */
type DailyStats = Pick<WatchListUpdate, 'avgVolume' | 'yearStartPrice' | 'oneYearPrice' | 'threeYearPrice' | 'fiveYearPrice'>;

const DAILY_FIELDS: (keyof DailyStats)[] = ['avgVolume', 'yearStartPrice', 'oneYearPrice', 'threeYearPrice', 'fiveYearPrice'];

const dirClassRules = {
    'rzx-dir--up': ({data: record}) => record.data.priceChange > 0,
    'rzx-dir--down': ({data: record}) => record.data.priceChange < 0
};

export class WatchlistExpandedModel extends HoistModel {
    apiTask = TaskObserver.trackLast();
    @observable accessor isOpen = false;
    @managed gridModel = this.createGridModel();
    private watchListId: number;
    /** Daily statistics by ticker, from the snapshot. Merged into each live update, which leaves them out. */
    private dailyStats = new Map<string, DailyStats>();

    // One heatmap scale shared by all the return columns, so the same return gets the same color in
    // every column. Rebuilt on each load from the returns in the current watchlist. No color until then.
    private returnScale: (v: number) => string = () => null;

    @action
    show(id: number) {
        this.watchListId = id;
        this.loadWatchlistAsync().linkTo(this.apiTask);
        XH.messageHub.subscribe('watchlist', this.xhId, message => this.onWatchlistUpdate(message));
        this.isOpen = true;
    }

    @action
    close() {
        XH.messageHub.unSubscribe('watchlist', this.xhId);
        this.isOpen = false;
    }

    private async loadWatchlistAsync() {
        if (!this.watchListId) return;
        const watchList = await XH.watchListService.activateWatchlistAsync(this.watchListId);
        XH.intradayService.loadTickersAsync(watchList.updates.map(u => u.ticker));

        const updates = watchList.updates.map(u => this.toRow(u, u.price));

        this.returnScale = buildHeatmapScale(updates.flatMap(u => [u.ytdChangePct, u.oneYearChangePct, u.threeYearChangePct, u.fiveYearChangePct]));

        this.gridModel.loadData(updates);
    }

    private onWatchlistUpdate(message: NotificationMessage) {
        switch (message.envelope.eventType) {
            case 'UPDATE': {
                const watchlistUpdates = message.payload as WatchListUpdates;
                if (watchlistUpdates.watchlistId != this.watchListId) return;
                this.updateWatchlist(watchlistUpdates);
                break;
            }
            case 'RELOAD':
                this.loadWatchlistAsync();
                break;
        }
    }

    private updateWatchlist(watchList: WatchListUpdates) {
        // Today's bars, for the Price Move sparklines
        XH.intradayService.applyWatchlistBars(watchList.updates);

        // Every row is rebuilt in full - Store.updateData nulls out any field a row leaves out.
        // Carry the last price along so the Last cell can flash on this tick.
        const {store} = this.gridModel,
            updates = watchList.updates.map(u => this.toRow(u, store.getById(u.ticker)?.data.price ?? u.price));
        this.gridModel.store.updateData(updates);
    }

    /** A grid row from a snapshot or live quote, with the derived columns. Bid and ask are still simulated. */
    private toRow(u: WatchListUpdate, prevPrice: number) {
        const {ticker, price, priceChange, volume} = u,
            daily = this.mergeDailyStats(u),
            change = (from: number) => (from && price != null ? price / from - 1 : null);

        return {
            ...u,
            prevPrice,
            prevClose: price != null && priceChange != null ? price - priceChange : null,
            ...simulateBidAsk(ticker, price),
            relVolume: volume != null && daily.avgVolume ? volume / daily.avgVolume : null,
            ytdChangePct: change(daily.yearStartPrice),
            oneYearChangePct: change(daily.oneYearPrice),
            threeYearChangePct: change(daily.threeYearPrice),
            fiveYearChangePct: change(daily.fiveYearPrice)
        };
    }

    /** Keeps the daily statistics a snapshot carries, and returns the latest ones for the ticker. */
    private mergeDailyStats(u: WatchListUpdate): DailyStats {
        const ret = {...this.dailyStats.get(u.ticker)};
        DAILY_FIELDS.forEach(f => {
            if (u[f] !== undefined) ret[f] = u[f];
        });
        this.dailyStats.set(u.ticker, ret);
        return ret;
    }

    private createGridModel(): GridModel {
        return new GridModel({
            store: {
                idSpec: 'ticker',
                fields: [
                    'ticker',
                    'securityName',
                    {name: 'price', type: 'number'},
                    {name: 'prevPrice', type: 'number'},
                    {name: 'priceChange', type: 'number'},
                    {name: 'priceChangePct', type: 'number'},
                    {name: 'bid', type: 'number'},
                    {name: 'ask', type: 'number'},
                    {name: 'volume', type: 'number'},
                    {name: 'relVolume', type: 'number'},
                    {name: 'dayLow', type: 'number'},
                    {name: 'dayHigh', type: 'number'},
                    {name: 'prevClose', type: 'number'},
                    {name: 'yearLow', type: 'number'},
                    {name: 'yearHigh', type: 'number'},
                    {name: 'ytdChangePct', type: 'number'},
                    {name: 'oneYearChangePct', type: 'number'},
                    {name: 'threeYearChangePct', type: 'number'},
                    {name: 'fiveYearChangePct', type: 'number'}
                ]
            },
            sizingMode: 'compact',
            showHover: true,
            rowBorders: true,
            columns: [
                {
                    field: 'ticker',
                    headerName: 'Symbol',
                    width: 78,
                    pinned: true,
                    cellClass: 'wl-symbol',
                    cellClassRules: dirClassRules,
                    tooltip: (v, {record}) => record.data.securityName ?? v
                },
                {
                    colId: 'priceMove',
                    field: 'ticker',
                    width: 160,
                    headerName: 'Price Move',
                    headerTooltip: "Today's price, from the prior close, in 5-minute bars",
                    sortable: false,
                    filterable: false,
                    excludeFromExport: true,
                    cellClass: MINI_CHART_CELL,
                    // Observes the ticker's series, so it redraws on each new bar on its own
                    renderer: ticker => intradaySparkline({ticker, renderer: sparklineRenderer})
                },
                {
                    field: 'price',
                    headerName: 'Last',
                    width: 70,
                    align: 'right',
                    renderer: priceRenderer({precision: 2}),
                    cellClass: 'wl-last',
                    cellClassRules: {
                        ...dirClassRules,
                        'flash-green': ({data: record}) => record.data.price > record.data.prevPrice,
                        'flash-red': ({data: record}) => record.data.price < record.data.prevPrice
                    },
                    tooltip: (v, {record}) => `Bid ${record.data.bid?.toFixed(2)} × Ask ${record.data.ask?.toFixed(2)}`
                },
                {field: 'bid', width: 70, align: 'right', renderer: priceRenderer({precision: 2}), cellClass: 'wl-bid'},
                {field: 'ask', width: 70, align: 'right', renderer: priceRenderer({precision: 2}), cellClass: 'wl-ask'},
                {
                    field: 'priceChange',
                    headerName: 'Net Chg',
                    width: 96,
                    align: 'right',
                    absSort: true,
                    // Two lines of change at the compact font size - sets the row height for the whole grid
                    rowHeight: 26,
                    renderer: changeBlockRenderer,
                    rendererIsComplex: true,
                    cellClassRules: {
                        'wl-fill--up': ({data: record}) => record.data.priceChange > 0,
                        'wl-fill--down': ({data: record}) => record.data.priceChange < 0
                    }
                },
                {field: 'volume', width: 70, align: 'right', renderer: v => compactNumberFormatter(v), cellClass: 'rzx-amber'},
                {
                    field: 'relVolume',
                    headerName: 'RVol',
                    width: 106,
                    renderer: relVolumeRenderer,
                    headerTooltip: 'Relative volume - today vs. its average'
                },
                {
                    colId: 'dayRange',
                    field: 'dayHigh',
                    headerName: 'Day Range',
                    width: 130,
                    sortable: false,
                    renderer: dayRangeRenderer,
                    rendererIsComplex: true,
                    cellClass: MINI_CHART_CELL
                },
                {
                    colId: 'yearRange',
                    field: 'yearHigh',
                    headerName: '1Y Range',
                    width: 130,
                    sortable: false,
                    renderer: yearRangeRenderer,
                    rendererIsComplex: true,
                    cellClass: MINI_CHART_CELL
                },
                this.heatmapCol('ytdChangePct', 'YTD'),
                this.heatmapCol('oneYearChangePct', '1Y'),
                this.heatmapCol('threeYearChangePct', '3Y'),
                this.heatmapCol('fiveYearChangePct', '5Y')
            ]
        });
    }

    private heatmapCol(field: string, headerName: string) {
        return {
            field,
            width: 80,
            align: 'right' as const,
            headerName,
            renderer: (v: number) => percentPlainFormatter(v),
            agOptions: {
                cellStyle: (params: {value: number}) => {
                    const color = this.returnScale(params.value);
                    return color ? {backgroundColor: color} : null;
                }
            }
        };
    }
}
